import { useMutation, useQuery } from '@tanstack/react-query';

import { backup, config } from '../api/client';
import { useToast } from './Toast';
import { Badge, Button, ErrorState, Spinner } from './ui';
import { downloadBlob } from '../lib/csv';

/**
 * Settings → Backup (ADMIN): one button that pulls GET /backup and saves the
 * answer under the filename the api chose. The JSON carries every table in the
 * system plus the object-storage KEYS of the stored bills — the bucket bytes
 * themselves are a separate backup (the note below says so, because the single
 * most expensive mistake here would be assuming the file is everything).
 */
export function BackupPanel() {
  const { toast } = useToast();
  const publicConfig = useQuery({
    queryKey: ['config', 'public'],
    queryFn: ({ signal }) => config.public(signal),
    staleTime: 60_000,
  });

  const download = useMutation({
    mutationFn: () => backup.download(),
    onSuccess: async ({ blob, filename }) => {
      downloadBlob(blob, filename, 'application/json');
      let summary = '';
      try {
        const snapshot = JSON.parse(await blob.text()) as {
          meta?: { counts?: Record<string, number> };
        };
        const counts = snapshot.meta?.counts;
        if (counts) {
          summary = ` — ${counts.subscriptions} subscriptions, ${counts.paymentEvents} payments, ${counts.billFiles} bills`;
        }
      } catch {
        // The download already succeeded; a summary is decoration.
      }
      toast(`Backup downloaded${summary}`, 'success');
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const storage = publicConfig.data?.s3;

  return (
    <div className="card" style={{ maxWidth: 720 }}>
      <h2 className="card__title">Backup</h2>
      <p className="cell-muted" style={{ margin: '0 0 var(--space-3)' }}>
        One JSON file with every table in the system: subscriptions and their assigned
        users, encrypted credentials, money accounts, payments and transactions, bill
        files with their object-storage keys, the balance ledger, notifications,
        reports, spending drafts, the full audit trail and the access accounts.
      </p>

      <dl className="env-list">
        <div className="env-list__row">
          <dt className="env-list__key">Bill objects (bucket)</dt>
          <dd className="env-list__value">
            {publicConfig.isPending ? (
              <Spinner label="Reading configuration" />
            ) : publicConfig.isError ? (
              <ErrorState message={publicConfig.error.message} onRetry={() => void publicConfig.refetch()} />
            ) : storage?.configured ? (
              <>
                {storage.bucket ?? '—'} <Badge tone="ok">configured</Badge>
              </>
            ) : (
              <Badge tone="bad">not configured</Badge>
            )}
          </dd>
        </div>
        <div className="env-list__row">
          <dt className="env-list__key">Object storage</dt>
          <dd className="env-list__value">
            {storage?.endpoint ?? '—'} <span className="cell-muted">({storage?.region ?? '—'})</span>
          </dd>
        </div>
      </dl>

      <ul className="cell-muted" style={{ margin: 'var(--space-3) 0', paddingLeft: 'var(--space-4)' }}>
        <li>
          The file carries the <strong>keys</strong> of the stored bills (for example{' '}
          <code>bills/2026/10/&lt;uuid&gt;.pdf</code>) — the objects themselves are not
          in it. Back the bucket up separately.
        </li>
        <li>
          Credentials are stored as ciphertext; restoring them needs the original{' '}
          <code>CREDENTIALS_ENCRYPTION_KEY</code>. Secret values are never part of a
          backup — only the list of names a restore must provision.
        </li>
        <li>Every export is written to the audit trail.</li>
      </ul>

      <Button
        variant="primary"
        loading={download.isPending}
        onClick={() => download.mutate()}
      >
        Download backup (JSON)
      </Button>
    </div>
  );
}
